import { FC, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import CtaButton from '@components/quiz/shared/CtaButton';
import Icon from '@components/atoms/icon';
import { updateProfileFields } from '@api-service/profile';
import type { UpdateProfileFields } from '@api-service/profile';
import { fetchEntryLeagues } from '@api-service/fpl-entries';
import { AnalyticsEvents, captureAnalyticsEvent } from '@lib/analytics/events';
import { MAX_FPL_TEAM_ID } from '@lib/constants';
import {
  DEADLINE_DISPLAY_FORMAT,
  resolveChangeDeadlineAt
} from '@lib/mini-league-promise';
import { useAccessToken } from '@hooks/useAccessToken';
import { useLeagueContext } from '@hooks/useLeagueContext';
import { useProfile, USERS_ME_QUERY_KEY } from '@hooks/useProfile';

const ANALYTICS_PLACEMENT = 'my_mini_league_page';

const DIGITS_ONLY = /^\d+$/;

const toPositiveId = (value: string, max = Number.MAX_SAFE_INTEGER) => {
  if (!DIGITS_ONLY.test(value.trim())) return null;
  const parsed = parseInt(value.trim(), 10);
  return parsed > 0 && parsed <= max ? parsed : null;
};

const FIELD_CLASSES =
  'w-full min-w-0 rounded-lg border border-neutral-300 px-4 py-3 font-open-sans text-base text-neutral-900 focus:border-primary-600 focus:outline-none focus:ring-[3px] focus:ring-primary-600/10';

const SECONDARY_BUTTON_CLASSES =
  'rounded-lg border border-neutral-300 bg-white font-open-sans font-bold text-neutral-800 transition-colors hover:border-neutral-400 disabled:cursor-not-allowed disabled:border-neutral-200 disabled:bg-neutral-100 disabled:text-neutral-400';

const StepLabel: FC<{
  number: number;
  htmlFor: string;
  isActive: boolean;
  children: string;
}> = ({ number, htmlFor, isActive, children }) => (
  <div className="mb-2 flex items-center gap-2.5">
    <span
      className={`flex h-[22px] w-[22px] items-center justify-center rounded-full font-open-sans text-xs font-bold ${
        isActive
          ? 'bg-neutral-200 text-neutral-700'
          : 'bg-neutral-100 text-neutral-400'
      }`}
    >
      {number}
    </span>
    <label
      htmlFor={htmlFor}
      className={`font-open-sans text-sm font-bold ${
        isActive ? 'text-neutral-800' : 'text-neutral-400'
      }`}
    >
      {children}
    </label>
  </div>
);

/**
 * The standalone Mini-League Promise card on /my-mini-league (FFH-2732): the
 * email campaign's direct landing for setting or changing the nomination.
 * Unlike the account section it also handles first-time capture; reaching
 * the page from the campaign email is the qualification, so any signed-in
 * member can nominate.
 *
 * Both fields stay editable, so a corrected FPL ID must be re-searched
 * before saving (the same drift guard as the funnel and account forms) and a
 * league chosen for one entry can never be saved against another. Changes
 * and first-time nominations both close at the gameweek 3 deadline
 * (FFH-2672); the lock is UI-only and the API stays permissive, mirroring
 * the account page. While FPL is in maintenance the stored league's name
 * lookup is skipped and the ID stands in (FFH-2623).
 */
const MyMiniLeagueCard: FC = () => {
  const {
    profile,
    isLoading: isProfileLoading,
    isError: hasProfileFailed,
    refetch: refetchProfile
  } = useProfile();
  const { accessToken } = useAccessToken();
  const { context } = useLeagueContext();
  const queryClient = useQueryClient();

  const [hasOpenedChangeForm, setHasOpenedChangeForm] = useState(false);
  const [fplIdInput, setFplIdInput] = useState('');
  const [lookupFplId, setLookupFplId] = useState<number | null>(null);
  const [selectedLeagueId, setSelectedLeagueId] = useState('');
  const [manualLeagueInput, setManualLeagueInput] = useState('');
  const [isManualEntryChosen, setIsManualEntryChosen] = useState(false);

  const savedFplTeamId = profile?.fplTeamId ?? null;
  const savedMiniLeagueId = profile?.miniLeagueId ?? null;
  const isSaved = savedMiniLeagueId !== null;
  const isEditing = !isSaved || hasOpenedChangeForm;

  const isFplInMaintenance = context?.isFplUpdating === true;

  // A member with an FPL ID but no league yet (the funnel captured the ID
  // and they left before nominating) opens straight on step 2.
  useEffect(() => {
    if (!isSaved && savedFplTeamId !== null && lookupFplId === null) {
      setFplIdInput(String(savedFplTeamId));
      setLookupFplId(savedFplTeamId);
    }
  }, [isSaved, savedFplTeamId, lookupFplId]);

  // Discard any previously selected/entered league whenever the FPL ID being
  // looked up changes, so a re-lookup for a different team can never carry
  // over a selection that belonged to the old one.
  useEffect(() => {
    setSelectedLeagueId('');
    setManualLeagueInput('');
    setIsManualEntryChosen(false);
  }, [lookupFplId]);

  const storedLeaguesQuery = useQuery({
    queryKey: ['fpl-entry-leagues', savedFplTeamId],
    queryFn: () => fetchEntryLeagues(savedFplTeamId as number),
    enabled: !isFplInMaintenance && savedFplTeamId !== null && isSaved,
    retry: false,
    refetchOnWindowFocus: false
  });

  const lookupQuery = useQuery({
    queryKey: ['fpl-entry-leagues', lookupFplId],
    queryFn: () => fetchEntryLeagues(lookupFplId as number),
    enabled: isEditing && lookupFplId !== null,
    retry: false,
    refetchOnWindowFocus: false
  });

  const mutation = useMutation({
    mutationFn: async (fields: UpdateProfileFields) => {
      // accessToken is fetched independently of profile and can still be
      // null here; the save button gates on it, but guard again so a stale
      // click never sends an unauthenticated PUT.
      if (!accessToken) throw new Error('No access token available');

      const result = await updateProfileFields(accessToken, fields);
      if (!result.ok) throw new Error(result.message);
      return result.profile;
    },
    onSuccess: () => {
      // useProfile keys on [USERS_ME_QUERY_KEY, accessToken]; invalidating by
      // the prefix refreshes whichever token-scoped entry it is reading.
      queryClient.invalidateQueries({ queryKey: USERS_ME_QUERY_KEY });
      setHasOpenedChangeForm(false);
    }
  });

  // Unlike the funnel's capture block, this page has nothing else to show, so
  // a profile that never arrives has to offer a way out rather than leaving a
  // skeleton pulsing forever.
  if (hasProfileFailed) {
    return (
      <section
        data-cy="my-mini-league-profile-error"
        className="rounded-2xl border border-neutral-200 bg-white p-6 text-center sm:p-10"
      >
        <p className="font-open-sans text-base text-neutral-800">
          We couldn’t load your account just now.
        </p>
        <button
          type="button"
          data-cy="my-mini-league-retry"
          className={`${SECONDARY_BUTTON_CLASSES} mt-4 px-5 py-3 text-[15px]`}
          onClick={() => refetchProfile()}
        >
          Try again
        </button>
      </section>
    );
  }

  if (isProfileLoading || !profile) {
    return (
      <div
        data-cy="my-mini-league-skeleton"
        className="h-40 animate-pulse rounded-2xl border border-neutral-200 bg-white"
      />
    );
  }

  const changeDeadlineAt = resolveChangeDeadlineAt(context);
  const isChangeOpen =
    changeDeadlineAt !== null && Date.now() < Date.parse(changeDeadlineAt);
  const deadlineDisplay =
    changeDeadlineAt !== null
      ? dayjs(changeDeadlineAt).format(DEADLINE_DISPLAY_FORMAT)
      : null;

  const storedLeagues = storedLeaguesQuery.data ?? [];
  const storedLeagueName =
    storedLeagues.find((league) => league.leagueId === savedMiniLeagueId)
      ?.name ?? null;

  const effectiveFplTeamId = toPositiveId(fplIdInput, MAX_FPL_TEAM_ID);
  // True once the FPL ID that was actually looked up (whose leagues/fallback
  // are on screen) still matches the current input. False the moment the
  // input drifts away from it (edited after a lookup, before re-searching);
  // without this, save could pair the new FPL ID with a league selected for
  // the old one.
  const isLookupCurrentForInput =
    lookupFplId !== null && lookupFplId === effectiveFplTeamId;
  const canLookUpLeagues =
    effectiveFplTeamId !== null && lookupFplId !== effectiveFplTeamId;

  const leagues = lookupQuery.data ?? [];
  const hasLookupFailed =
    lookupQuery.isError || (lookupQuery.isSuccess && leagues.length === 0);
  const showLeagueList =
    isLookupCurrentForInput &&
    !lookupQuery.isFetching &&
    leagues.length > 0 &&
    !isManualEntryChosen;
  const showManualEntry =
    isLookupCurrentForInput &&
    !lookupQuery.isFetching &&
    (hasLookupFailed || isManualEntryChosen);

  // Pre-select the stored league without an effect: until the member picks,
  // the select shows the stored value whenever the lookup that produced the
  // list was for the saved FPL ID and listed it. A lookup for a corrected ID
  // never inherits the old selection.
  const isStoredLeagueListed =
    isSaved &&
    lookupFplId === savedFplTeamId &&
    leagues.some((league) => league.leagueId === savedMiniLeagueId);
  const selectValue =
    selectedLeagueId || (isStoredLeagueListed ? String(savedMiniLeagueId) : '');

  const chosenMiniLeagueId = showManualEntry
    ? toPositiveId(manualLeagueInput)
    : toPositiveId(selectValue);

  const canSubmit =
    isChangeOpen &&
    effectiveFplTeamId !== null &&
    chosenMiniLeagueId !== null &&
    isLookupCurrentForInput &&
    !!accessToken &&
    !mutation.isLoading;

  const findLabel = lookupQuery.isFetching
    ? 'Finding…'
    : isLookupCurrentForInput
    ? 'Found'
    : 'Find leagues';

  const handleStartChange = () => {
    mutation.reset();
    // The saved FPL ID seeds the input and the lookup, so the stored league
    // can be listed and pre-selected for the member to change either value.
    setFplIdInput(String(savedFplTeamId ?? ''));
    setLookupFplId(savedFplTeamId);
    setHasOpenedChangeForm(true);
  };

  const handleSave = () => {
    // canSubmit already gates the button on these; re-checked so a stale
    // click can never pair a drifted FPL ID with an old league choice, and a
    // save started after the deadline passed mid-edit is dropped rather than
    // written.
    if (
      !isChangeOpen ||
      effectiveFplTeamId === null ||
      chosenMiniLeagueId === null ||
      !isLookupCurrentForInput
    ) {
      return;
    }

    captureAnalyticsEvent(AnalyticsEvents.miniLeaguePromiseSaved, {
      placement: ANALYTICS_PLACEMENT,
      league_source: showManualEntry ? 'manual' : 'lookup',
      is_change: isSaved
    });

    // The PUT is full-replace on both fields, so a corrected FPL ID and/or
    // mini league overwrites the saved values.
    mutation.mutate({
      fplTeamId: effectiveFplTeamId,
      miniLeagueId: chosenMiniLeagueId
    });
  };

  const heading = isEditing
    ? 'Lock in your mini-league'
    : 'Your mini-league is locked in';
  const subtext = isEditing
    ? 'Tell us the mini-league you want to win and we’ll hold you to it.'
    : 'This is the league we’ll hold you to. You can change it any time before the Gameweek 3 deadline.';

  return (
    <section
      data-cy="my-mini-league-card"
      className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-100"
    >
      <div className="h-1 bg-primary-600" />
      <div className="p-6 sm:p-10">
        <p className="mb-2.5 font-sans text-xs font-bold uppercase tracking-[0.14em] text-primary-600">
          Mini-League Promise
        </p>
        <h1 className="font-sans text-3xl font-extrabold leading-[1.2] text-neutral-900">
          {heading}
        </h1>
        <p className="mt-3 font-open-sans text-base leading-relaxed text-neutral-600">
          {subtext}
        </p>

        {isEditing ? (
          <div className="mt-7 flex flex-col gap-6">
            <div>
              <StepLabel number={1} htmlFor="my-mini-league-fpl-id" isActive>
                Your FPL ID
              </StepLabel>
              <div className="flex flex-col gap-2.5 sm:flex-row">
                <input
                  id="my-mini-league-fpl-id"
                  data-cy="my-mini-league-fpl-id"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="e.g. 1234567"
                  className={`${FIELD_CLASSES} sm:flex-1`}
                  value={fplIdInput}
                  onChange={(event) =>
                    setFplIdInput(event.target.value.replace(/\D/g, ''))
                  }
                />
                <button
                  type="button"
                  data-cy="my-mini-league-find"
                  className={`${SECONDARY_BUTTON_CLASSES} px-5 py-3 text-[15px]`}
                  onClick={() =>
                    effectiveFplTeamId !== null &&
                    setLookupFplId(effectiveFplTeamId)
                  }
                  disabled={!canLookUpLeagues}
                >
                  {findLabel}
                </button>
              </div>
              <p className="mt-2 font-open-sans text-[13px] leading-normal text-neutral-500">
                Find it in the URL of your FPL points page.
              </p>
              {!isLookupCurrentForInput &&
                lookupFplId !== null &&
                effectiveFplTeamId !== null && (
                  <p
                    data-cy="my-mini-league-stale-warning"
                    className="mt-2 font-open-sans text-sm text-warning-600"
                  >
                    You changed your FPL ID. Search again to update your
                    mini-leagues.
                  </p>
                )}
            </div>

            <div className="h-px bg-neutral-200" />

            <div>
              <StepLabel
                number={2}
                htmlFor="my-mini-league-league"
                isActive={isLookupCurrentForInput}
              >
                Choose your mini-league
              </StepLabel>

              {lookupQuery.isFetching && (
                <p className="font-open-sans text-sm text-neutral-600">
                  Looking up your mini-leagues…
                </p>
              )}

              {!lookupQuery.isFetching && !isLookupCurrentForInput && (
                <p className="rounded-lg border border-dashed border-neutral-300 bg-neutral-100 px-4 py-3 font-open-sans text-[15px] text-neutral-500">
                  Enter your FPL ID first and we’ll list your leagues.
                </p>
              )}

              {showLeagueList && (
                <>
                  <select
                    id="my-mini-league-league"
                    data-cy="my-mini-league-select"
                    className={`${FIELD_CLASSES} bg-white`}
                    value={selectValue}
                    onChange={(event) =>
                      setSelectedLeagueId(event.target.value)
                    }
                  >
                    <option value="" disabled>
                      Select a mini-league
                    </option>
                    {leagues.map((league) => (
                      <option key={league.leagueId} value={league.leagueId}>
                        {`${league.name} (${league.leagueId})`}
                      </option>
                    ))}
                  </select>
                  <p className="mt-2.5 font-open-sans text-[13px]">
                    <button
                      type="button"
                      data-cy="my-mini-league-use-manual"
                      className="text-primary-600 hover:text-primary-700"
                      onClick={() => setIsManualEntryChosen(true)}
                    >
                      Can’t see your league? Enter the ID manually
                    </button>
                  </p>
                </>
              )}

              {showManualEntry && (
                <>
                  <input
                    id="my-mini-league-league"
                    data-cy="my-mini-league-manual"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="e.g. 987654"
                    className={FIELD_CLASSES}
                    value={manualLeagueInput}
                    onChange={(event) =>
                      setManualLeagueInput(
                        event.target.value.replace(/\D/g, '')
                      )
                    }
                  />
                  <p className="mt-2 font-open-sans text-[13px] leading-normal text-neutral-500">
                    {hasLookupFailed
                      ? 'We couldn’t list your leagues yet. Paste the ID from your league’s URL on the FPL site instead.'
                      : 'Paste the ID from your league’s URL on the FPL site.'}
                  </p>
                </>
              )}
            </div>

            {!isChangeOpen && (
              <p
                data-cy="my-mini-league-closed"
                className="font-open-sans text-sm text-neutral-600"
              >
                The Mini-League Promise closed at the Gameweek 3 deadline, so
                nominations can no longer be changed.
              </p>
            )}

            <CtaButton
              onClick={handleSave}
              disabled={!canSubmit}
              dataCy="my-mini-league-save"
            >
              {mutation.isLoading ? 'Saving…' : 'Lock it in'}
            </CtaButton>

            {mutation.isError && (
              <p
                data-cy="my-mini-league-error"
                className="font-open-sans text-sm text-danger-600"
              >
                Something went wrong saving your mini-league. Please try again.
              </p>
            )}
          </div>
        ) : (
          <div className="mt-7">
            <div className="flex items-center gap-3.5 rounded-xl border border-success-100 bg-success-50 px-5 py-4">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-success-600 text-white">
                <Icon name="Check" className="h-4 w-4" aria-hidden="true" />
              </span>
              <div>
                <p
                  data-cy="my-mini-league-name"
                  className="font-open-sans text-base font-bold text-success-800"
                >
                  {storedLeagueName ?? `Mini-league #${savedMiniLeagueId}`}
                </p>
                <p className="mt-0.5 font-open-sans text-sm text-success-700">
                  {`League ID ${savedMiniLeagueId}`}
                  {savedFplTeamId !== null && ` · FPL ID ${savedFplTeamId}`}
                </p>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
              <p className="font-open-sans text-sm text-neutral-500">
                {isChangeOpen
                  ? `We’ll track this league all season. Changes close ${deadlineDisplay}.`
                  : 'Locked for the season. League changes closed at the Gameweek 3 deadline.'}
              </p>
              {/* A change needs a stored FPL ID to seed the form; a profile
                  without one gets no entry into a form it could never
                  pre-fill. */}
              {isChangeOpen && savedFplTeamId !== null && (
                <button
                  type="button"
                  data-cy="my-mini-league-change"
                  className={`${SECONDARY_BUTTON_CLASSES} px-4 py-2.5 text-sm`}
                  onClick={handleStartChange}
                >
                  Change league
                </button>
              )}
            </div>

            {mutation.isSuccess && (
              <p className="mt-3 font-open-sans text-sm font-bold text-success-700">
                Mini-league updated.
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default MyMiniLeagueCard;
